/**
 * Landing v3 — the phone's top bar (below `lg`; Figma 116:885 / 116:972, user
 * 2026-09-29). The desktop's two pieces of top chrome, in one fixed slot:
 *
 * - While the hero is up, the brand pill: the full logo, nothing else.
 * - Once the reader scrolls into the story, the bar: the mark, Study Tools ⌄,
 *   About Us and the language flag. The theme switch lives in the Study Tools
 *   menu (the frame has no button for it); a sign-up joins the bar only where
 *   the page has no floating one (About, on a computer). Whatever the width
 *   can't hold folds away: the menu's name shortens to "Features", then About
 *   Us moves into the menu, and last the menu shows only its icon.
 *
 * The exchange is scrubbed by scroll both ways, as on desktop, and both lift
 * clear off the top while the reader races back UP the page, settling back
 * when they stop (`useScrollTravel`, the desktop bar's own gate).
 */
import { Button, DropdownItem } from '@flashka/shared/ui';

import {
  motion,
  type MotionValue,
  useMotionValueEvent,
  useTransform,
} from 'framer-motion';
import { ChevronDownIcon, LayoutGridIcon } from 'lucide-react';
import { type RefObject, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import {
  AboutUsButton,
  clamp01,
  FlashkaWordmark,
  GetStartedCta,
  GLASS_PILL_CLASS,
  LanguageMenu,
  LogoMark,
  StudyToolsMenu,
  ThemeMenuItem,
  useGoToAbout,
} from './landing-chrome';
import { BAR_GAP, BAR_MARK_GAP, type BarFit, pickBarFit } from './landing-phone-layout';

/** The bar's footprint (Figma 116:972): 24px off the top, 48px tall around
 *  40px controls. The phone's pile pins its cards under it (landing-phone). */
export const PHONE_BAR_TOP = 24;
export const PHONE_BAR_HEIGHT = 48;

/** The bar's own padding and hairline (`pl-4 pr-2` + 1px each side). */
const BAR_PADDING = 16 + 8 + 2;

/**
 * Which `BarFit` the width takes, from the controls' natural widths (a hidden
 * row renders each one once — they change with the language, and when the
 * web font lands) and the widest the bar may grow.
 */
function useBarFit(
  slotRef: RefObject<HTMLDivElement | null>,
  measureRef: RefObject<HTMLDivElement | null>,
  language: string,
): BarFit {
  const [fit, setFit] = useState<BarFit>(1);
  useLayoutEffect(() => {
    const slot = slotRef.current;
    const row = measureRef.current;
    if (!slot || !row) return;
    const width = (key: string) =>
      row.querySelector<HTMLElement>(`[data-lv3-fit="${key}"]`)?.getBoundingClientRect()
        .width ?? 0;
    const decide = () =>
      setFit(
        pickBarFit(
          {
            studyTools: width('studyTools'),
            features: width('features'),
            icon: width('icon'),
            about: width('about'),
            flag: width('flag'),
            cta: width('cta'),
          },
          slot.clientWidth - BAR_PADDING,
        ),
      );
    decide();
    const observer = new ResizeObserver(decide);
    observer.observe(slot);
    void document.fonts?.ready.then(decide);
    return () => observer.disconnect();
  }, [slotRef, measureRef, language]);
  return fit;
}

export function PhoneTopBar({
  handoff,
  travelling,
  reduceMotion,
  showCta = false,
  onJumpTo,
  onLogoClick,
  logoLabel,
}: {
  /** 0 → the brand pill, 1 → the bar, scrubbed by the page's scroll. */
  handoff: MotionValue<number>;
  /** The fast-climb gate (`useScrollTravel`): 1 while the reader races up. */
  travelling: MotionValue<number>;
  reduceMotion: boolean;
  /** A Get Started in the bar, where the page has no floating sign-up. */
  showCta?: boolean;
  /** Land on feature `index` (the Study Tools menu). */
  onJumpTo: (index: number) => void;
  onLogoClick: () => void;
  /** What the logo does, for screen readers (back to the top by default). */
  logoLabel?: string;
}) {
  const { t, i18n } = useTranslation();
  const goToAbout = useGoToAbout();
  const slotRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const fit = useBarFit(slotRef, measureRef, i18n.language);

  // Only the half of the exchange that is showing takes a tap.
  const [barOn, setBarOn] = useState(() => handoff.get() >= 0.5);
  useMotionValueEvent(handoff, 'change', (v) => setBarOn(v >= 0.5));

  // A bar that carries the sign-up never travels away (user 2026-10-02: the
  // call to action is always on screen).
  const away = () => (showCta ? 1 : 1 - travelling.get());
  const logoOpacity = useTransform(() => (1 - clamp01(handoff.get() / 0.5)) * away());
  const barReveal = useTransform(() => clamp01((handoff.get() - 0.4) / 0.6));
  const barOpacity = useTransform(() => barReveal.get() * away());
  // The desktop bar's entrance (it settles down into place) and exit (it lifts
  // clear off the top while the reader travels).
  const lift = () => (showCta ? 0 : -120 * travelling.get());
  const logoY = useTransform(lift);
  const barY = useTransform(
    () => (reduceMotion ? 0 : -12 * (1 - barReveal.get())) + lift(),
  );

  const studyTools = t('landingV3.nav.studyTools');
  const features = t('landingV3.nav.featuresNav');
  const aboutUs = t('landingV3.nav.aboutUs');
  const getStarted = t('landingV3.nav.getStarted');
  const logoButtonLabel = logoLabel ?? t('landingV3.nav.backToTop');

  return (
    <header
      className="pointer-events-none fixed inset-x-0 z-20 flex justify-center px-3"
      style={{ top: PHONE_BAR_TOP }}>
      <div
        ref={slotRef}
        className="relative w-full max-w-[608px]"
        style={{ height: PHONE_BAR_HEIGHT }}>
        {/* The brand pill (Figma 116:889). Its fade sits on the glass itself,
            as the desktop bar's does: on a parent, the opacity would cut the
            glass off from the page behind and the blur would vanish mid-fade. */}
        <div className="absolute inset-x-0 top-0 flex justify-center">
          <motion.button
            type="button"
            onClick={onLogoClick}
            aria-label={logoButtonLabel}
            style={{ opacity: logoOpacity, y: logoY }}
            inert={barOn}
            className={cn(
              'flex h-12 cursor-pointer items-center gap-[6.6px] rounded-[20px] px-[13px]',
              GLASS_PILL_CLASS,
              !barOn && 'pointer-events-auto',
            )}>
            <LogoMark className="h-5 w-[18.4px]" flightTarget />
            <FlashkaWordmark className="h-[18px] w-auto text-secondary-700" />
          </motion.button>
        </div>

        {/* The bar (Figma 116:1188), as wide as its controls, centred. */}
        <div className="absolute inset-x-0 top-0 flex justify-center">
          <motion.div
            style={{ opacity: barOpacity, y: barY }}
            inert={!barOn}
            className={cn(
              // The brand pill's height and corners (user 2026-09-30).
              'flex h-12 max-w-full items-center rounded-[20px] py-1 pl-4 pr-2',
              GLASS_PILL_CLASS,
              barOn && 'pointer-events-auto',
            )}>
            <button
              type="button"
              onClick={onLogoClick}
              aria-label={logoButtonLabel}
              className="shrink-0 cursor-pointer">
              <LogoMark className="h-[22px] w-[20.3px]" />
            </button>
            <div
              className="flex min-w-0 items-center"
              style={{ gap: BAR_GAP, marginInlineStart: BAR_MARK_GAP }}>
              <StudyToolsMenu
                label={fit === 0 ? studyTools : features}
                icon={fit === 3 ? LayoutGridIcon : undefined}
                onJumpTo={onJumpTo}
                footer={
                  <>
                    {fit >= 2 && (
                      <DropdownItem onSelect={goToAbout}>{aboutUs}</DropdownItem>
                    )}
                    <ThemeMenuItem />
                  </>
                }
              />
              {fit < 2 && <AboutUsButton />}
            </div>
            {showCta && (
              <GetStartedCta placement="phoneBar" size="m" variant="primary">
                {getStarted}
              </GetStartedCta>
            )}
            {/* A flex box, not a line: an inline trigger sat on the text
                baseline, the flag 3px above the bar's middle. */}
            <span className="flex shrink-0 items-center pe-1.5 ps-[13px]">
              <LanguageMenu />
            </span>
          </motion.div>
        </div>

        {/* Each control once, at its natural width, for `useBarFit` — in a
            box of no size, so the row never widens the page. */}
        <div
          aria-hidden
          inert
          className="invisible absolute left-0 top-0 h-0 w-0 overflow-hidden">
          <div ref={measureRef} className="flex w-max items-center whitespace-nowrap">
            <span data-lv3-fit="studyTools" className="flex">
              <Button variant="ghost" size="m" rightIcon={ChevronDownIcon}>
                {studyTools}
              </Button>
            </span>
            <span data-lv3-fit="features" className="flex">
              <Button variant="ghost" size="m" rightIcon={ChevronDownIcon}>
                {features}
              </Button>
            </span>
            <span data-lv3-fit="icon" className="flex">
              <Button variant="ghost" size="m" icon={LayoutGridIcon} />
            </span>
            <span data-lv3-fit="about" className="flex">
              <Button variant="ghost" size="m">
                {aboutUs}
              </Button>
            </span>
            <span data-lv3-fit="flag" className="flex pe-1.5 ps-[13px]">
              <LanguageMenu />
            </span>
            {showCta && (
              <span data-lv3-fit="cta" className="flex">
                <Button variant="primary" size="m">
                  {getStarted}
                </Button>
              </span>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
